// Teil 19: Inhalt der Chat-Startseite - was der AI-Chat kann, mit Beispiel-Prompts. Rein (kein Vue, kein Netz), damit der Text
// an einer Stelle steht. `examples` kommt von POST /v2/sources/chat-examples und enthaelt je Bereich das NEUESTE Objekt des
// Kunden ({ monitoring: {name, domain}, ai: {name, brand, domain}, content: {title, keyword}, collection: {name} }); fehlt ein
// Bereich, steht im Prompt ein allgemeiner Platzhalter.
//
// Ein Thema: { id, icon, title, suite, text, uses, usesLabel, prompts }
//   suite: Name des Store-Flags (research_suite, monitoring_suite, ai_suite, content_suite) oder null (immer verfuegbar)
//   uses: Name des eigenen Objekts, mit dem die Beispiele gefuellt sind - null bei Platzhaltern
//   prompts: [{ text, parts: [{ text, own }], suite? }] - `own` markiert eigene Namen (fett), gesetzt beim Zusammenbauen statt
//            hinterher per Textsuche (ein Keyword wie "top" wuerde sonst auch in "top 10" hervorgehoben); `suite` = nur zeigen,
//            wenn diese Suite verfuegbar ist (Themen ohne eigene Suite wie "Do it for me")

const FALLBACK_DOMAIN = "example.com"
const FALLBACK_KEYWORD = "running shoes"

const OWN = "__ownValue"
// Eigener Wert des Kunden (wird hervorgehoben)
const own = (value) => ({ [OWN]: String(value) })

// Template-Tag: baut { text, parts } und kennzeichnet die mit own() eingesetzten Werte. Verschachtelte prompt-Ergebnisse
// werden mit ihren Teilen uebernommen.
function prompt(strings, ...values) {
    const parts = []
    const push = (text, isOwn) => {
        if (!text) return
        const last = parts[parts.length - 1]
        if (last && last.own === isOwn) last.text += text
        else parts.push({ text, own: isOwn })
    }
    strings.forEach((chunk, i) => {
        push(chunk, false)
        if (i >= values.length) return
        const value = values[i]
        if (value && typeof value === "object" && OWN in value) push(value[OWN], true)
        else if (value && typeof value === "object" && Array.isArray(value.parts)) value.parts.forEach((part) => push(part.text, part.own))
        else push(String(value), false)
    })
    return { text: parts.map((part) => part.text).join(""), parts }
}

export function buildCapabilities(examples) {
    const e = examples || {}
    const mon = e.monitoring || null
    const ai = e.ai || null
    const content = e.content || null
    const collection = e.collection || null

    const realDomain = (mon && mon.domain) || (ai && ai.domain) || null
    const domain = realDomain ? own(realDomain) : FALLBACK_DOMAIN
    const myDomain = realDomain ? own(realDomain) : "my domain"
    const keyword = content && content.keyword ? own(content.keyword) : FALLBACK_KEYWORD
    const monitoringRef = mon ? prompt`"${own(mon.name)}"` : "my monitoring project"
    const aiRef = ai ? prompt`"${own(ai.name)}"` : "my AI project"
    const contentRef = content ? prompt`"${own(content.title)}"` : "my content project"
    const collectionRef = collection ? prompt`"${own(collection.name)}"` : null

    return [
        {
            id: "rankings",
            icon: "mdi-chart-line",
            title: "Your rankings",
            suite: "monitoring_suite",
            text: "Winners, losers and opportunities in your tracked keywords - compared with your competitors.",
            uses: mon ? mon.name : null,
            usesLabel: "Based on your newest project",
            prompts: [
                prompt`Which keywords in ${monitoringRef} gained or lost the most positions since the last update?`,
                prompt`Which keywords in ${monitoringRef} are on page 2 and could reach the top 10?`,
                prompt`How has the visibility of ${monitoringRef} developed over the last 3 months compared to my competitors?`,
                prompt`Which competitors gained the most visibility in ${monitoringRef} recently?`
            ]
        },
        {
            id: "geo",
            icon: "mdi-robot-outline",
            title: "AI visibility",
            suite: "ai_suite",
            text: "How ChatGPT, Gemini, Perplexity & co. mention your brand and your competitors - and which sources they cite.",
            uses: ai ? ai.name : null,
            usesLabel: "Based on your newest project",
            prompts: [
                prompt`How visible is my brand in ${aiRef} compared with my competitors?`,
                prompt`Which prompts in ${aiRef} mention my competitors but not my brand?`,
                prompt`Which sources do AI assistants cite most often for the prompts in ${aiRef}?`,
                prompt`How does the visibility of my brand in ${aiRef} differ between ChatGPT, Gemini and Perplexity?`
            ]
        },
        {
            id: "content",
            icon: "mdi-text-box-edit-outline",
            title: "Content",
            suite: "content_suite",
            text: "Briefings, target terms, outlines and first drafts for your content projects.",
            uses: content ? content.title : null,
            usesLabel: "Based on your newest project",
            prompts: [
                prompt`Summarize the briefing and the most important terms of ${contentRef}`,
                prompt`Write an outline for ${contentRef} based on its target terms`,
                prompt`Which of my content projects are still open, and in which workflow stage are they?`,
                prompt`Create a content project for the keyword "${keyword}" in Germany`
            ]
        },
        {
            id: "keywords",
            icon: "mdi-magnify",
            title: "Keyword research",
            suite: "research_suite",
            text: "Search volume, keyword ideas, difficulty and search intent - for any topic or domain.",
            uses: collection ? collection.name : null,
            usesLabel: "Based on your newest collection",
            prompts: [
                prompt`Find keyword ideas for "${keyword}" in Germany with search volume and difficulty`,
                prompt`What is the search intent and difficulty of these keywords: ${keyword}, best ${keyword}, ${keyword} cheap?`,
                prompt`How has the search volume of "${keyword}" developed over the last 2 years?`,
                collectionRef ? prompt`Group the keywords of my collection ${collectionRef} by topic` : prompt`Which keywords does ${domain} rank for with the most search volume?`
            ]
        },
        {
            id: "competitors",
            icon: "mdi-account-group-outline",
            title: "Competitors",
            suite: "research_suite",
            text: "Who competes with you in Google, which keywords you share and how their visibility develops.",
            uses: realDomain,
            usesLabel: "Based on your domain",
            prompts: [
                prompt`Who are the organic competitors of ${domain} and which keywords do we share?`,
                prompt`How has the organic visibility of ${domain} developed over the last months?`,
                prompt`Which keywords does my biggest competitor rank for that ${domain} does not?`,
                prompt`Which technologies does ${domain} use?`
            ]
        },
        {
            id: "backlinks",
            icon: "mdi-link-variant",
            title: "Backlinks",
            suite: "research_suite",
            text: "Backlink profiles, referring domains, anchor texts and link gaps - for you and your competitors.",
            uses: realDomain,
            usesLabel: "Based on your domain",
            prompts: [
                prompt`Analyze the backlink profile of ${domain}: strongest referring domains and anchor texts`,
                prompt`Which websites link to my competitors but not to ${domain}?`,
                prompt`How many backlinks did ${domain} win and lose in the last 6 months?`,
                prompt`Compare the backlink authority of ${domain} with its top 3 competitors`
            ]
        },
        {
            id: "technical",
            icon: "mdi-speedometer",
            title: "Technical SEO",
            suite: "research_suite",
            text: "On-page audits, Lighthouse performance tests and live Google results for any page.",
            uses: realDomain,
            usesLabel: "Based on your domain",
            prompts: [
                prompt`Run an on-page audit for https://${domain}/ and list the most important fixes`,
                prompt`Run a Lighthouse test for https://${domain}/ and show the biggest performance problems`,
                prompt`Who ranks on Google right now for "${keyword}" in Germany?`,
                prompt`Extract the headings and main content of https://${domain}/`
            ]
        },
        {
            id: "actions",
            icon: "mdi-lightning-bolt-outline",
            title: "Do it for me",
            suite: null,
            text: "Quaro can also set things up for you. Deleting and publishing always wait for your confirmation.",
            uses: null,
            prompts: [
                { ...prompt`Create a collection with keyword ideas for "${keyword}" in Germany`, suite: "research_suite" },
                {
                    ...(collectionRef
                        ? prompt`Set up a monitoring project for ${myDomain} with the keywords from my collection ${collectionRef}`
                        : prompt`Set up a monitoring project for ${myDomain} with the 20 most important keywords`),
                    suite: "monitoring_suite"
                },
                { ...prompt`Add 10 buying-intent prompts to ${ai ? prompt`my AI project ${aiRef}` : "my AI project"}`, suite: "ai_suite" },
                { ...prompt`Create a report for ${ai ? prompt`my AI project ${aiRef}` : "my AI project"} with the visibility trend of my brand`, suite: "ai_suite" },
                { ...prompt`Create a content project for the keyword "${keyword}" in Germany`, suite: "content_suite" }
            ]
        }
    ]
}

// Beispiele eines Themas, die zur verfuegbaren Suite passen (hoechstens 4). Gesperrte Themen zeigen alle Beispiele (ausgegraut).
export function visiblePrompts(topic, isAvailable) {
    const list = topic && Array.isArray(topic.prompts) ? topic.prompts : []
    if (!topic || !topic.available) return list.slice(0, 4)
    return list.filter((p) => !p.suite || isAvailable(p.suite)).slice(0, 4)
}

// Kurze Erklaerung unter der Ueberschrift: was der Chat anders macht als eine Suchmaschine.
export const CAPABILITY_FACTS = [
    { icon: "mdi-folder-account-outline", text: "Uses your own Quaro projects and live SEO data" },
    { icon: "mdi-shield-check-outline", text: "Changes only after your confirmation" },
    { icon: "mdi-counter", text: "Every answer shows the credits it used" }
]

// Ist die Suite fuer diesen Nutzer sichtbar? Dieselbe Regel wie das Hauptmenue (App.vue): Nutzerrecht oder Admin UND die Suite der
// Company ist aktiv bzw. im Test (AI-Suite auch im kostenlosen Plan). Research hat dort nur das Nutzerrecht.
const SUITE_STATUS = {
    monitoring_suite: { status: "company_status_monitoring_suite", allowed: ["active", "testing"] },
    content_suite: { status: "company_status_content_suite", allowed: ["active", "testing"] },
    ai_suite: { status: "company_status_ai_suite", allowed: ["active", "testing", "free"] }
}

export function isSuiteAvailable(state, suite) {
    if (!suite) return true
    const s = state || {}
    if (suite === "research_suite") return !!s.research_suite
    const rule = SUITE_STATUS[suite]
    if (!rule) return false
    return (!!s[suite] || !!s.admin) && rule.allowed.includes(s[rule.status])
}

// Platzhalter, die in Beispielen ohne eigene Daten stehen - nach dem Klick markiert, damit sie sofort ueberschrieben werden.
export const PLACEHOLDERS = [FALLBACK_DOMAIN, FALLBACK_KEYWORD]

// Erste Platzhalter-Stelle im Text als [start, end] - oder null.
export function placeholderRange(text) {
    const value = String(text || "")
    let best = null
    for (const placeholder of PLACEHOLDERS) {
        const at = value.indexOf(placeholder)
        if (at >= 0 && (best === null || at < best[0])) best = [at, at + placeholder.length]
    }
    return best
}
